import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../store/authStore";
import htaLogo from "../../../assets/icons/hta_logo.jpeg";
import {
	Bars3Icon,
	XMarkIcon,
	HomeIcon,
	ChartBarIcon,
	UserCircleIcon,
	ArrowRightOnRectangleIcon,
	TicketIcon,
	CalendarIcon,
	CogIcon,
	TruckIcon,
	WrenchScrewdriverIcon,
	ComputerDesktopIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	BellIcon,
} from "@heroicons/react/24/outline";
import ingestApi from "../../../lib/ingestApi";

export default function Layout({ children }) {
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [notificationOpen, setNotificationOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [notificationLoading, setNotificationLoading] = useState(false);
	const [openMenus, setOpenMenus] = useState(() => {
		// 로컬 스토리지에서 저장된 메뉴 상태 불러오기
		const savedOpenMenus = localStorage.getItem("openMenus");
		return savedOpenMenus ? JSON.parse(savedOpenMenus) : ["home"];
	});
	const { user, logout } = useAuthStore();
	const navigate = useNavigate();

	// 현재 경로 가져오기
	const location = window.location.pathname;

	// 메뉴 상태가 변경될 때마다 로컬 스토리지에 저장
	useEffect(() => {
		localStorage.setItem("openMenus", JSON.stringify(openMenus));
	}, [openMenus]);

	// 현재 경로에 따라 메뉴 자동 열기
	useEffect(() => {
		// 현재 경로에 해당하는 메뉴 찾기
		const currentPath = location.split("/")[1]; // 첫 번째 경로 세그먼트 (예: /ticket/register -> ticket)
		if (currentPath && !openMenus.includes(currentPath)) {
			setOpenMenus((prev) => [...prev, currentPath]);
		}
	}, [location]);

	useEffect(() => {
		let timer;
		const loadUnread = async () => {
			if (!user) return;
			try {
				const response = await ingestApi.getUnreadNotifications();
				setUnreadCount(response.data?.unread_count || 0);
			} catch (error) {
				setUnreadCount(0);
			}
		};
		loadUnread();
		timer = setInterval(loadUnread, 30000);
		return () => clearInterval(timer);
	}, [user]);

	const handleLogout = () => {
		logout();
		navigate("/login");
	};

	const loadNotifications = async () => {
		setNotificationLoading(true);
		try {
			const response = await ingestApi.listNotifications({ limit: 10, offset: 0 });
			setNotifications(response.data?.items || []);
			setUnreadCount(response.data?.unread_count || 0);
		} catch (error) {
			setNotifications([]);
		} finally {
			setNotificationLoading(false);
		}
	};

	const toggleNotifications = async () => {
		const nextOpen = !notificationOpen;
		setNotificationOpen(nextOpen);
		if (nextOpen) {
			await loadNotifications();
		}
	};

	const markNotificationRead = async (notificationId) => {
		try {
			await ingestApi.markNotificationRead(notificationId);
			await loadNotifications();
		} catch (error) {
			// ignore
		}
	};

	// 메뉴 토글 함수
	const toggleMenu = (menuId) => {
		if (openMenus.includes(menuId)) {
			setOpenMenus(openMenus.filter((id) => id !== menuId));
		} else {
			setOpenMenus([...openMenus, menuId]);
		}
	};

	// 메뉴 구조 정의
	const navigation = [
		{
			id: "home",
			name: "홈",
			href: "/",
			icon: HomeIcon,
			current: location === "/",
		},
		{
			id: "ticket",
			name: "승차권 관리",
			icon: TicketIcon,
			current: location.startsWith("/ticket"),
			submenu: [
				{
					name: "승차권 등록",
					href: "/ticket/register",
					current: location === "/ticket/register",
				},
				{
					name: "승차권 등록 현황",
					href: "/ticket/status",
					current: location === "/ticket/status",
				},
				{
					name: "일자별 승차권 보유 현황",
					href: "/ticket/daily",
					current: location === "/ticket/daily",
				},
			],
		},
		{
			id: "event",
			name: "행사 정리",
			icon: CalendarIcon,
			current: location.startsWith("/event"),
			submenu: [
				{
					name: "좌석 배정",
					href: "/event/seat",
					current: location === "/event/seat",
				},
				{
					name: "문자 발송",
					href: "/event/sms",
					current: location === "/event/sms",
				},
				{
					name: "예약건별 배정 관리",
					href: "/event/reservation",
					current: location === "/event/reservation",
				},
			],
		},
		{
			id: "management",
			name: "기타 관리",
			icon: CogIcon,
			current: location.startsWith("/management"),
			submenu: [
				{
					name: "일정표 등록",
					href: "/management/schedule",
					current: location === "/management/schedule",
				},
				{
					name: "전화상담 관리",
					href: "/management/call",
					current: location === "/management/call",
				},
				{
					name: "발송된 문자 관리",
					href: "https://console.solapi.com/message-log/detail",
					current: false,
					isExternal: true,
				},
				{
					name: "로그 조회",
					href: "/management/log",
					current: location === "/management/log",
				},
				{
					name: "좌석배치도 열차종 관리",
					href: "/management/seatmap-train-types",
					current: location === "/management/seatmap-train-types",
				},
				{
					name: "좌석배치도 편집",
					href: "/management/seatmap-editor",
					current: location === "/management/seatmap-editor",
				},
			],
		},
		{
			id: "bus",
			name: "버스 좌석 배정",
			icon: TruckIcon,
			current: location.startsWith("/bus"),
			submenu: [
				{
					name: "버스 좌석 등록",
					href: "/bus/info",
					current: location === "/bus/info",
				},
				{
					name: "버스 좌석배치",
					href: "/bus/seat",
					current: location === "/bus/seat",
				},
				{
					name: "버스별 배치확정 리스트",
					href: "/bus/confirmed",
					current: location === "/bus/confirmed",
				},
			],
		},
		{
			id: "utility",
			name: "기타 편의기능",
			icon: WrenchScrewdriverIcon,
			current: location.startsWith("/utility"),
			submenu: [
				{
					name: "열차번호 조회",
					href: "http://aiblue.ddns.net/",
					current: false,
					isExternal: true,
				},
				{
					name: "열차 운행 정보 생성",
					href: "http://aiblue.ddns.net/schedule-v2",
					current: false,
					isExternal: true,
				},
			],
		},
	];

	return (
		<div className="min-h-screen bg-gray-100">
			{/* 모바일 사이드바 */}
			<div
				className={`fixed inset-0 z-40 lg:hidden ${
					sidebarOpen ? "block" : "hidden"
				}`}
				role="dialog"
				aria-modal="true"
			>
				<div
					className="fixed inset-0 bg-gray-600 bg-opacity-0"
					aria-hidden="true"
					onClick={() => setSidebarOpen(false)}
				></div>

				<div className="fixed inset-y-0 left-0 w-64 flex flex-col bg-white shadow-xl">
					<div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
						<div className="flex-1 flex justify-between items-center">
							<div className="flex items-center">
								<img
									src={htaLogo}
									alt="홍익여행사 로고"
									className="h-8 w-8 mr-2 rounded-md"
								/>
								<h2 className="text-lg font-bold text-gray-900">
									홍익여행사
								</h2>
							</div>
						</div>
						<button
							type="button"
							className="-mr-2 h-10 w-10 bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:bg-gray-100 focus:outline-none"
							onClick={() => setSidebarOpen(false)}
						>
							<span className="sr-only">사이드바 닫기</span>
							<XMarkIcon className="h-6 w-6" aria-hidden="true" />
						</button>
					</div>

					<div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
						<nav className="mt-5 flex-1 px-2 space-y-1">
							{navigation.map((item) => (
								<div key={item.id}>
									{item.id === "home" ? (
										<Link
											to={item.href}
											className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
												item.current
													? "bg-gray-100 text-blue-600"
													: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
											}`}
										>
											<item.icon
												className={`mr-3 flex-shrink-0 h-6 w-6 ${
													item.current
														? "text-blue-600"
														: "text-gray-400 group-hover:text-gray-500"
												}`}
												aria-hidden="true"
											/>
											{item.name}
										</Link>
									) : (
										<>
											<button
												onClick={() =>
													toggleMenu(item.id)
												}
												className={`w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
													item.current
														? "bg-gray-100 text-blue-600"
														: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
												}`}
											>
												<div className="flex items-center">
													<item.icon
														className={`mr-3 flex-shrink-0 h-6 w-6 ${
															item.current
																? "text-blue-600"
																: "text-gray-400 group-hover:text-gray-500"
														}`}
														aria-hidden="true"
													/>
													{item.name}
												</div>
												{openMenus.includes(item.id) ? (
													<ChevronDownIcon className="h-5 w-5" />
												) : (
													<ChevronRightIcon className="h-5 w-5" />
												)}
											</button>
											{openMenus.includes(item.id) &&
												item.submenu && (
													<div className="pl-10 pt-1 pb-1 space-y-1">
														{item.submenu.map(
															(subItem) =>
																subItem.isExternal ? (
																	<a
																		key={
																			subItem.name
																		}
																		href={
																			subItem.href
																		}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="group flex items-center px-2 py-1.5 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
																	>
																		{
																			subItem.name
																		}
																	</a>
																) : (
																	<Link
																		key={
																			subItem.name
																		}
																		to={
																			subItem.href
																		}
																		className={`group flex items-center px-2 py-1.5 text-sm font-medium rounded-md ${
																			subItem.current
																				? "bg-gray-100 text-blue-600"
																				: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
																		}`}
																	>
																		{
																			subItem.name
																		}
																	</Link>
																)
														)}
													</div>
												)}
										</>
									)}
								</div>
							))}
						</nav>
					</div>
				</div>
			</div>

			{/* 데스크톱 사이드바 */}
			<div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
				<div className="flex flex-col flex-grow border-r border-gray-200 bg-white overflow-y-auto">
					<div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-gray-200">
						<img
							src={htaLogo}
							alt="홍익여행사 로고"
							className="h-8 w-8 mr-2 rounded-md"
						/>
						<h2 className="text-lg font-bold text-gray-900">
							홍익여행사
						</h2>
					</div>
					<div className="flex-grow flex flex-col">
						<nav className="flex-1 px-2 pb-4 space-y-1 mt-5">
							{navigation.map((item) => (
								<div key={item.id}>
									{item.id === "home" ? (
										<Link
											to={item.href}
											className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
												item.current
													? "bg-gray-100 text-blue-600"
													: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
											}`}
										>
											<item.icon
												className={`mr-3 flex-shrink-0 h-6 w-6 ${
													item.current
														? "text-blue-600"
														: "text-gray-400 group-hover:text-gray-500"
												}`}
												aria-hidden="true"
											/>
											{item.name}
										</Link>
									) : (
										<>
											<button
												onClick={() =>
													toggleMenu(item.id)
												}
												className={`w-full group flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md ${
													item.current
														? "bg-gray-100 text-blue-600"
														: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
												}`}
											>
												<div className="flex items-center">
													<item.icon
														className={`mr-3 flex-shrink-0 h-6 w-6 ${
															item.current
																? "text-blue-600"
																: "text-gray-400 group-hover:text-gray-500"
														}`}
														aria-hidden="true"
													/>
													{item.name}
												</div>
												{openMenus.includes(item.id) ? (
													<ChevronDownIcon className="h-5 w-5" />
												) : (
													<ChevronRightIcon className="h-5 w-5" />
												)}
											</button>
											{openMenus.includes(item.id) &&
												item.submenu && (
													<div className="pl-10 pt-1 pb-1 space-y-1">
														{item.submenu.map(
															(subItem) =>
																subItem.isExternal ? (
																	<a
																		key={
																			subItem.name
																		}
																		href={
																			subItem.href
																		}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="group flex items-center px-2 py-1.5 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
																	>
																		{
																			subItem.name
																		}
																	</a>
																) : (
																	<Link
																		key={
																			subItem.name
																		}
																		to={
																			subItem.href
																		}
																		className={`group flex items-center px-2 py-1.5 text-sm font-medium rounded-md ${
																			subItem.current
																				? "bg-gray-100 text-blue-600"
																				: "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
																		}`}
																	>
																		{
																			subItem.name
																		}
																	</Link>
																)
														)}
													</div>
												)}
										</>
									)}
								</div>
							))}
						</nav>
					</div>
					<div className="flex-shrink-0 flex flex-col border-t border-gray-200">
						<div className="border-t border-gray-200 p-3 text-center">
							<p className="text-xs text-gray-500">
								Copyright &copy; 2025 AIBlue Inc.
							</p>
							<p className="text-xs text-gray-500">
								All rights reserved.
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* 메인 콘텐츠 */}
			<div className="lg:pl-64 flex flex-col flex-1">
				<div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
					<button
						type="button"
						className="px-4 border-r border-gray-200 text-gray-500 lg:hidden"
						onClick={() => setSidebarOpen(true)}
					>
						<span className="sr-only">사이드바 열기</span>
						<Bars3Icon className="h-6 w-6" aria-hidden="true" />
					</button>
					<div className="flex-1 px-4 flex justify-between">
						<div className="flex-1 flex items-center">
							<h1 className="text-lg font-semibold text-gray-900">
								티켓 관리 시스템 v2.0
							</h1>
						</div>
						<div className="ml-4 flex items-center md:ml-6">
							<div className="flex items-center space-x-4">
								<div className="relative">
									<button
										type="button"
										onClick={toggleNotifications}
										className="relative inline-flex items-center justify-center h-10 w-10 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100"
										aria-label="알림"
									>
										<BellIcon className="h-5 w-5" />
										{unreadCount > 0 && (
											<span className="absolute -top-1 -right-1 h-5 min-w-[1.25rem] px-1 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
												{unreadCount}
											</span>
										)}
									</button>
									{notificationOpen && (
										<div className="absolute right-0 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
											<div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
												<span className="text-sm font-semibold text-gray-900">알림</span>
												<span className="text-xs text-gray-500">
													미확인 {unreadCount}건
												</span>
											</div>
											<div className="max-h-72 overflow-y-auto">
												{notificationLoading ? (
													<div className="px-4 py-6 text-sm text-gray-500 text-center">
														불러오는 중...
													</div>
												) : notifications.length === 0 ? (
													<div className="px-4 py-6 text-sm text-gray-500 text-center">
														새 알림이 없습니다.
													</div>
												) : (
													notifications.map((item) => (
														<div
															key={item.id}
															className={`px-4 py-3 border-b border-gray-100 ${
																item.read_at ? "bg-white" : "bg-blue-50"
															}`}
														>
															<div className="text-sm font-medium text-gray-900">
																세션 #{item.session_id || "-"} OCR 완료
															</div>
															<div className="text-xs text-gray-600 mt-1">
																상태: {item.payload?.status || item.type}
															</div>
															<div className="text-xs text-gray-400 mt-1">
																{item.created_at
																	? new Date(item.created_at).toLocaleString("ko-KR")
																	: ""}
															</div>
															{!item.read_at && (
																<button
																	onClick={() => markNotificationRead(item.id)}
																	className="mt-2 text-xs text-blue-600 hover:text-blue-800"
																>
																	읽음 처리
																</button>
															)}
														</div>
													))
												)}
											</div>
										</div>
									)}
								</div>
								<div className="flex items-center bg-blue-50 px-3 py-1.5 rounded-lg">
									<UserCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
									<span className="text-sm font-medium text-blue-700">
										{user?.id || "사용자"}
									</span>
								</div>
								<button
									onClick={handleLogout}
									className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out shadow-sm hover:shadow-md"
								>
									<ArrowRightOnRectangleIcon className="h-4 w-4 mr-1.5" />
									로그아웃
								</button>
							</div>
						</div>
					</div>
				</div>

				<main className="flex-1">
					<div className="py-6">
						<div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
							{children}
						</div>
					</div>
				</main>
			</div>
		</div>
	);
}
